import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { LabelAndSelectValue, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { BetterDocConsultationReasonSelect } from "@/modules/patients/detail/better-doc/components/better-doc-consultation-reason-select"
import {
  type BetterDocSpecialty,
  getBetterDocSubSpecialty,
} from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import { BetterDocSpecialistSearchFormSchema } from "@/modules/patients/detail/better-doc/schema/better-doc-specialist-search-schema"

const SubSpecialtySelect = ({ specialtyName }: { specialtyName: BetterDocSpecialty }) => {
  const { t } = useTranslation()
  const { control } = useFormContext<BetterDocSpecialistSearchFormSchema>()

  const subSpecialtiesList = getBetterDocSubSpecialty(specialtyName)

  if (subSpecialtiesList.length === 0) {
    return null
  }

  return (
    <div className="w-full">
      <Controller
        control={control}
        name="subSpecialty"
        render={({ field: { value, onChange } }) => (
          <div className="relative">
            <Select value={value} onValueChange={(v) => onChange(v)}>
              <SelectTrigger data-testid="sub-specialty-select-trigger">
                <LabelAndSelectValue
                  label={t("episodeOfCare.betterDoc.searchDialog.subSpecialty")}
                  value={value ? t(`episodeOfCare.betterDoc.searchDialog.subSpecialties.${value.toUpperCase()}`) : ""}
                />
              </SelectTrigger>
              <SelectContent className="max-w-(--radix-select-trigger-width)">
                <ScrollArea className="max-h-64">
                  <div className="py-2">
                    {subSpecialtiesList.map((option) => (
                      <SelectItem key={option} value={option} data-testid={`sub-specialty-option-${option}`}>
                        {t(`episodeOfCare.betterDoc.searchDialog.subSpecialties.${option.toUpperCase()}`)}
                      </SelectItem>
                    ))}
                  </div>
                </ScrollArea>
              </SelectContent>
            </Select>
            {value && (
              <Button
                variant="circle"
                className="absolute top-1/2 right-10 h-10 w-10 -translate-y-1/2"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onChange(undefined)
                }}>
                <X />
              </Button>
            )}
          </div>
        )}
      />
    </div>
  )
}

const FixedBetterDocSpecialtyField = ({ specialtyName }: { specialtyName: BetterDocSpecialty }) => {
  const { t } = useTranslation()

  return (
    <Input
      label={t("episodeOfCare.progressNote.pointOfCare.specialty")}
      value={t(`episodeOfCare.betterDoc.searchDialog.specialties.${specialtyName}`)}
      disabled
      testId="better-doc-specialty-field"
    />
  )
}

type MedicalInformationProps = {
  specialtyName: BetterDocSpecialty
  isBetterDocSpecialtyFixed: boolean
}

const MedicalInformation = ({ specialtyName, isBetterDocSpecialtyFixed }: MedicalInformationProps) => {
  const { t } = useTranslation()

  return (
    <div className="grid w-full gap-4">
      <div className="text-on-surface text-base leading-6 font-medium">
        {t("episodeOfCare.betterDoc.searchDialog.medicalInformation")}
      </div>
      <BetterDocConsultationReasonSelect />
      {isBetterDocSpecialtyFixed && <FixedBetterDocSpecialtyField specialtyName={specialtyName} />}
      <SubSpecialtySelect specialtyName={specialtyName} />
    </div>
  )
}

export { MedicalInformation }
